"use client";

import { cn } from "@/lib/utils";
import type { Participant } from "../base-match-template";
import {
  CourtSvg,
  courtLines,
  courtQuad,
  createCourtProjector,
  getRacketMatchState,
  PAINT_LINE,
  PropShadow,
  RacketMatchFrame,
  RacketScore,
  useSvgIdPrefix,
} from "./racket-court-kit";

export type TableTennisTablePreviewProps = {
  mode: "singles" | "doubles";
  yourTeam: Participant[];
  opponentTeam: Participant[];
  yourWon?: boolean;
  showLabels?: boolean;
  linkToProfiles?: boolean;
  matchScore?: { yourGames: number; opponentGames: number };
  games?: Array<{ yourScore: number; opponentScore: number }>;
};

// ─── Table geometry (ITTF arena: blue table on a red floor) ─────────────────
// The tabletop is the focal plane; the net overhangs each side by 15.25cm and
// its clamps hang off the table edges — well inside the frame.

const P = createCourtProjector({
  yFar: 80,
  yNear: 164,
  farHalfWidth: 96,
  nearHalfWidth: 116,
});

const APRON = 7;
const NET_OVERHANG = 0.1;
const NET_HEIGHT = 9;

const [NL, NY] = P(0, 1);
const [NR] = P(1, 1);
const [FL, FY] = P(0, 0);
const [FR] = P(1, 0);

const TABLETOP = courtQuad(P, 0, 0, 1, 1);
const APRON_FACE = `M${NL},${NY}L${NR},${NY}L${NR},${NY + APRON}L${NL},${NY + APRON}Z`;
const EDGE_LINES = courtLines(P, [
  [0, 0, 1, 0],
  [1, 0, 1, 1],
  [1, 1, 0, 1],
  [0, 1, 0, 0],
]);
const CENTER_LINE = courtLines(P, [[0, 0.5, 1, 0.5]]);
const NET_SHADOW = courtQuad(P, 0.5, 0, 0.53, 1);
const [, NET_FAR_Y] = P(0.5, -NET_OVERHANG);
const [, NET_NEAR_Y] = P(0.5, 1 + NET_OVERHANG);
// Floor shadow: the table's footprint, a little bigger and pushed toward us.
const FLOOR_SHADOW = `M${FL - 6},${FY + 22}L${FR + 6},${FY + 22}L${NR + 18},${NY + 44}L${NL - 18},${NY + 44}Z`;
const LEGS = [0.17, 0.83].map((u) => P(u, 0.92)[0]);
const FAR_LEGS = [0.17, 0.83].map((u) => P(u, 0.1)[0]);

function TableTennisArt() {
  const id = useSvgIdPrefix();
  return (
    <>
      <CourtSvg>
        <defs>
          <linearGradient id={`${id}-arena`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#03060f" />
            <stop offset="1" stopColor="#0a1630" />
          </linearGradient>
          <linearGradient id={`${id}-floor`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#8f2630" />
            <stop offset="0.5" stopColor="#6e1a24" />
            <stop offset="1" stopColor="#3f0c14" />
          </linearGradient>
          <linearGradient id={`${id}-top`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#2d86d6" />
            <stop offset="0.5" stopColor="#1a64ac" />
            <stop offset="1" stopColor="#0e4580" />
          </linearGradient>
          <radialGradient id={`${id}-shadow`} cx="0.5" cy="0.5" r="0.5">
            <stop offset="0" stopColor="rgba(0,0,0,0.6)" />
            <stop offset="1" stopColor="rgba(0,0,0,0)" />
          </radialGradient>
          <pattern
            id={`${id}-mesh`}
            width="2"
            height="2"
            patternUnits="userSpaceOnUse"
          >
            <rect width="2" height="2" fill="rgba(10,14,30,0.82)" />
            <rect y="1.4" width="2" height="0.4" fill="rgba(140,170,220,0.4)" />
          </pattern>
        </defs>

        {/* Arena and the surround barriers. */}
        <rect width="300" height="58" fill={`url(#${id}-arena)`} />
        <rect y="38" width="300" height="20" fill="#0d2a55" />
        <rect y="38" width="300" height="1.5" fill="rgba(170,205,255,0.45)" />
        {[14, 74, 196, 254].map((x) => (
          <rect
            key={x}
            x={x}
            y="45"
            width="34"
            height="5"
            rx="1"
            fill="rgba(235,244,255,0.42)"
          />
        ))}
        <rect y="57" width="300" height="2" fill="rgba(0,0,0,0.5)" />

        {/* Red sports floor with a soft spill of light under the table. */}
        <rect y="59" width="300" height="161" fill={`url(#${id}-floor)`} />
        <path d={FLOOR_SHADOW} fill={`url(#${id}-shadow)`} />

        {/* Legs, then the slab: apron face + lit blue top + white edges. */}
        {FAR_LEGS.map((x) => (
          <rect
            key={`far-${x}`}
            x={x - 2}
            y={FY + 4}
            width="4"
            height="22"
            fill="#0b0f18"
          />
        ))}
        {LEGS.map((x) => (
          <rect
            key={x}
            x={x - 3}
            y={NY + APRON}
            width="6"
            height="30"
            rx="1"
            fill="#161c28"
          />
        ))}
        <path d={APRON_FACE} fill="#0a2d55" />
        <path
          d={`M${NL},${NY + APRON}L${NR},${NY + APRON}`}
          {...PAINT_LINE}
          stroke="rgba(0,0,0,0.5)"
          strokeWidth="1"
        />
        <path d={TABLETOP} fill={`url(#${id}-top)`} />
        <path
          d={EDGE_LINES}
          {...PAINT_LINE}
          stroke="rgba(255,255,255,0.96)"
          strokeWidth="2"
        />
        <path
          d={CENTER_LINE}
          {...PAINT_LINE}
          stroke="rgba(255,255,255,0.7)"
          strokeWidth="1"
        />

        {/* Net: shadow on the table, mesh, top tape, the two clamps. */}
        <path d={NET_SHADOW} fill="rgba(2,12,30,0.3)" />
        <rect
          x="148.2"
          y={NET_FAR_Y - NET_HEIGHT}
          width="3.6"
          height={NET_NEAR_Y - NET_FAR_Y}
          fill={`url(#${id}-mesh)`}
        />
        <rect
          x="147.6"
          y={NET_FAR_Y - NET_HEIGHT}
          width="1.4"
          height={NET_NEAR_Y - NET_FAR_Y}
          fill="#f3f6fb"
        />
        <rect
          x="146.5"
          y={NET_FAR_Y - NET_HEIGHT - 2}
          width="7"
          height="9"
          rx="1.2"
          fill="#1a1f2b"
        />
        <rect
          x="145.5"
          y={NET_NEAR_Y - NET_HEIGHT - 2}
          width="9"
          height="18"
          rx="1.5"
          fill="#1a1f2b"
        />
        <rect
          x="145.5"
          y={NET_NEAR_Y - NET_HEIGHT - 2}
          width="2.6"
          height="18"
          rx="1"
          fill="rgba(255,255,255,0.18)"
        />
      </CourtSvg>

      {/* Lighting: one hard overhead spot pooled on the table. */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_48%_42%_at_42%_44%,rgba(205,230,255,0.24),transparent_72%)]" />
      <div className="absolute inset-x-0 top-0 h-[50px] bg-[radial-gradient(ellipse_30%_90%_at_50%_0%,rgba(190,215,255,0.18),transparent_70%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_60%,rgba(20,2,6,0.45)_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_90%_110%_at_50%_45%,transparent_55%,rgba(0,0,0,0.45)_100%)]" />
    </>
  );
}

const PingPongBall = ({ className }: { className?: string }) => (
  <div
    className={cn(
      "absolute rounded-full bg-[radial-gradient(circle_at_34%_30%,#fff4e0_0%,#ffa340_30%,#ff7b00_60%,#c35400_100%)] shadow-[0_0_10px_rgba(255,140,30,0.45)]",
      className,
    )}
  />
);

/** A pen-hold paddle: red rubber blade, wooden handle. */
const Paddle = ({ className }: { className?: string }) => (
  <div className={cn("absolute h-[40px] w-[30px]", className)}>
    <div className="absolute top-[24px] left-1/2 h-[16px] w-[8px] -translate-x-1/2 rounded-b-[3px] bg-[linear-gradient(90deg,#8a5a2b,#d8a467_45%,#8a5a2b)]" />
    <div className="absolute top-0 left-0 h-[28px] w-[30px] rounded-[50%] bg-[#e9d3b0]" />
    <div className="absolute top-[1px] left-[1px] h-[26px] w-[28px] rounded-[50%] bg-[radial-gradient(circle_at_35%_30%,#ff5b54,#d3191f_55%,#8f0d13)] shadow-[inset_-2px_-3px_5px_rgba(0,0,0,0.3)]" />
  </div>
);

export function TableTennisTablePreview({
  mode: _mode,
  yourTeam,
  opponentTeam,
  yourWon,
  linkToProfiles = false,
  matchScore,
  games,
}: TableTennisTablePreviewProps) {
  const opponentWon = opponentTeam.length > 0 && yourWon === false;
  const yourIsWinner = !!yourWon && !opponentWon;

  const hasScore =
    !!matchScore &&
    (matchScore.yourGames > 0 ||
      matchScore.opponentGames > 0 ||
      (games?.length ?? 0) > 0);
  const matchState = getRacketMatchState(yourTeam, opponentTeam, hasScore);

  return (
    <RacketMatchFrame
      label="Table Tennis"
      frameClassName="bg-[#3f0c14]"
      matchState={matchState}
      scene={<TableTennisArt />}
      decor={
        matchState === "FULL" ? (
          <>
            <PropShadow className="top-[90%] left-[33%] h-[5px] w-[16px]" />
            <PingPongBall className="top-[86.5%] left-[34.5%] h-[9px] w-[9px]" />
          </>
        ) : (
          <>
            {/* Paddle resting on the table; ball mid-rally above it. */}
            <PropShadow className="top-[65%] left-[29%] h-[10px] w-[34px]" />
            <Paddle className="top-[52%] left-[29%] rotate-[-62deg]" />
            <PropShadow className="top-[58%] left-[60%] h-[6px] w-[16px]" />
            <div className="absolute top-[37.5%] left-[51%] h-[5px] w-[34px] rotate-[-14deg] rounded-full bg-[linear-gradient(90deg,transparent,rgba(255,190,120,0.45))]" />
            <PingPongBall className="top-[33%] left-[61.5%] h-[12px] w-[12px]" />
          </>
        )
      }
      yourTeam={yourTeam}
      opponentTeam={opponentTeam}
      yourWinner={yourIsWinner}
      opponentWinner={opponentWon}
      linkToProfiles={linkToProfiles}
      center={
        matchState === "FULL" && matchScore ? (
          <RacketScore
            left={matchScore.yourGames}
            right={matchScore.opponentGames}
            leftWinner={yourIsWinner}
            rightWinner={opponentWon}
            breakdown={games}
            accentClassName="bg-[#ff7b00] shadow-[0_0_12px_rgba(255,123,0,0.75)]"
          />
        ) : null
      }
    />
  );
}
